/**
 * createI18n —— 框架无关的初始化工厂
 *
 * 抽离自 apps/web 与 apps/accounts 中 `setupI18n` 里完全相同的编排逻辑：
 * 1. 解析 locale（cookie → navigator → 默认 zh-CN）
 * 2. 拉取 manifest（cache-busting + 重试）
 * 3. 构造 I18nKit 实例
 * 4. 等待 i18next 初始化完成
 * 5. 测试环境合并后端 API 词条
 * 6. 返回安全代理（未初始化时 $t 返回 key 本身）
 *
 * 本模块不含任何 Vue 依赖（SDK 也可使用）；Vue 注入留在各 app。
 */
import { CookieStorage } from '@peerup/utils'

import type { CreateI18nOptions, EnvTag, I18nHandle, I18nProxy, SupportedLocale } from './types'

import I18nKit from './index'
import { loadApiLocale, loadLocaleManifest } from './loaders'

/** 默认开启 API 词条加载的环境（与两个 app 原有逻辑一致） */
const DEFAULT_API_LOCALE_ENVS = ['dev', 'sit', 'sit1', 'pre']

/** 从浏览器语言映射到支持的 locale */
function detectNavigatorLocale(): SupportedLocale {
  // eslint-disable-next-line no-restricted-globals
  return typeof navigator !== 'undefined' && navigator.language.includes('zh') ? 'zh-CN' : 'en-US'
}

/** 默认的 API 词条加载判定 */
const defaultEnableApiLocale = (env: string): boolean => DEFAULT_API_LOCALE_ENVS.includes(env)

/** 实例未初始化时的兜底翻译（返回 key 本身） */
const fakeT = (key: string): string => key

/**
 * 创建并初始化 I18nKit 实例
 *
 * @returns `{ instance, locale, proxy }`
 * - `instance`：已 await 初始化完成的 I18nKit 实例
 * - `proxy`：安全代理对象，即使实例延迟初始化也能安全调用 `$t` / `setLocale` / `getCurrentLanguage`
 */
export async function createI18n(options: CreateI18nOptions): Promise<I18nHandle> {
  const {
    localeBasePath = '',
    version,
    projectType,
    apiLocaleUrl,
    enableApiLocale = defaultEnableApiLocale,
    staticOrigin,
    renderers,
    fallbackLocale = 'zh-CN',
    onLanguageChange,
  } = options

  // 环境标签
  const resolvedEnvTag = options.envTag ?? ''
  const storage = new CookieStorage({ namespace: 'i18n_kit' })

  // 1. 解析 locale：cookie → navigator → 默认 zh-CN
  const cookieLocale = storage.get(resolvedEnvTag) ?? 'zh-CN'
  const locale = (cookieLocale || detectNavigatorLocale()) as SupportedLocale

  // 2. 拉取 manifest（hash 用于 cache-busting）
  const manifest = await loadLocaleManifest({ basePath: localeBasePath, version })
  const hash = manifest[locale] ?? ''

  // 3. 构造实例
  const instance = new I18nKit({
    defaultLocale: locale,
    fallbackLocale,
    staticOrigin,
    renderers,
    backend: {
      loadPath: (languages: string[]) =>
        `${localeBasePath}/locales/${languages[0]}.${hash}.json`,
    },
    envTag: resolvedEnvTag as EnvTag,
    onLanguageChange,
  })

  // 4. 等待 i18next 初始化完成
  await new Promise<void>(resolve => {
    const i18next = instance.getI18next()
    if (i18next?.isInitialized) {
      resolve()
    } else {
      i18next?.on('initialized', () => resolve())
    }
  })

  // 5. 测试环境合并后端 API 词条
  const shouldLoadApi
    = projectType
      && (typeof enableApiLocale === 'function' ? enableApiLocale(resolvedEnvTag) : enableApiLocale)
  if (shouldLoadApi) {
    await loadApiLocale(instance, { locale, projectType, url: apiLocaleUrl })
  }

  // 6. 构造安全代理
  const proxy: I18nProxy = {
    $t: instance.$t.bind(instance),
    setLocale: (l: SupportedLocale) => instance.setLocale(l),
    getCurrentLanguage: () => instance.getCurrentLanguage(),
  }

  return { instance, locale, proxy }
}

// 重新导出 fakeT，供 app 在初始化前需要本地兜底翻译时复用
export { fakeT }
